/**
 * Fraud Settings Service (2026-08-27) — persists the tunable fraud/checkout-window thresholds so the
 * shop can adjust them WITHOUT a code change (the gap flagged after SLIK #3). Saved as a partial over
 * DEFAULT_FRAUD_CONFIG via shopSettingsService type 'fraudConfig' (same pattern as commissionPay /
 * reviewSettings). A live cache lets the checkout read the current config synchronously.
 *
 * Consumers: components/settings/FraudSecurityEditor.tsx (edit) + components/SmartCheckoutModal.tsx
 * (reads getFraudConfig() at checkout and passes it to evaluateCheckoutWindow + analyzeDiscountRequest).
 */
import { DEFAULT_FRAUD_CONFIG, type FraudDetectionConfig } from './fraudDetectionService';
import { shopSettingsService } from './shopSettingsService';

const TYPE = 'fraudConfig';

let cache: FraudDetectionConfig = { ...DEFAULT_FRAUD_CONFIG };
let loaded = false;

/** Synchronous best-effort config (defaults until the first async load completes). */
export function getCachedFraudConfig(): FraudDetectionConfig {
  return cache;
}

/** Load the saved partial (if any), merge over defaults, cache, return. Safe to call repeatedly. */
export async function getFraudConfig(): Promise<FraudDetectionConfig> {
  try {
    const saved = await shopSettingsService.getSettingsByType<Partial<FraudDetectionConfig>>(TYPE);
    cache = { ...DEFAULT_FRAUD_CONFIG, ...(saved || {}) };
    loaded = true;
  } catch (e) {
    console.error('[fraudSettings] load failed — using defaults', e);
    if (!loaded) cache = { ...DEFAULT_FRAUD_CONFIG };
  }
  return cache;
}

/** Persist a partial override (merged over the current config) + refresh the cache. */
export async function saveFraudConfig(partial: Partial<FraudDetectionConfig>): Promise<void> {
  const merged = { ...cache, ...partial };
  await shopSettingsService.saveSettingsByType(TYPE, merged);
  cache = merged;
  loaded = true;
}
